import { useState } from 'react';
import { Link, NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import { LoginDialog } from '@/components/LoginDialog/LoginDialog';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { useCatalog } from '@/context/CatalogContext';
import { useWishlist } from '@/context/WishlistContext';
import { paths } from '@/routes/paths';
import styles from './Layout.module.css';

export function Header() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { categories } = useCatalog();
  const cart = useCart();
  const wishlist = useWishlist();
  const { user, isSignedIn, signOut } = useAuth();

  // Signing in is offered on its own, not only as a step inside checkout: it is
  // what makes a reader's own purchases visible, whether or not they buy today.
  const [signingIn, setSigningIn] = useState(false);

  // Typing in the header search drops you into the all-products listing, as in
  // the prototype. Reading back from the URL keeps the field in sync on back/forward.
  const handleSearch = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('q', value);
    else next.delete('q');
    navigate(`${paths.category('all')}?${next}`, { replace: true });
  };

  return (
    <header className={styles.header}>
      <div className={styles.headerInner}>
        <Link to={paths.home} className={styles.logo}>
          <div className={styles.logoMark} aria-hidden="true">
            T
          </div>
          <span className={styles.logoText}>TUBMarket</span>
        </Link>

        <div className={styles.searchWrap}>
          <label className="sr-only" htmlFor="site-search">
            Search products
          </label>
          <input
            id="site-search"
            type="search"
            className={styles.search}
            value={searchParams.get('q') ?? ''}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search books, tees, tiny dinos…"
          />
        </div>

        <nav className={styles.nav}>
          {categories.map((c) => (
            <NavLink
              key={c.id}
              to={paths.category(c.id)}
              className={({ isActive }) =>
                `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`
              }
            >
              {c.name}
            </NavLink>
          ))}
        </nav>

        <div className={styles.actions}>
          <Link to={paths.wishlist} className={styles.iconBtn} title="Wishlist">
            <span aria-hidden="true">♡</span>
            <span className="sr-only">Wishlist</span>
            {wishlist.count > 0 && <span className={styles.wishBadge}>{wishlist.count}</span>}
          </Link>

          {isSignedIn ? (
            <div className={styles.account}>
              <Link to={paths.orders} className={styles.accountName} title="Your purchases">
                {user.username}
              </Link>
              <button type="button" className={styles.linkBtn} onClick={signOut}>
                Sign out
              </button>
            </div>
          ) : (
            <button
              type="button"
              className={styles.linkBtn}
              onClick={() => setSigningIn(true)}
            >
              Sign in
            </button>
          )}

          <button type="button" className={styles.cartBtn} onClick={cart.open}>
            Cart
            <span className={styles.cartCount}>{cart.count}</span>
          </button>
        </div>
      </div>

      <LoginDialog
        open={signingIn}
        reason="Sign in to see everything you've bought and open it in playTUB."
        onClose={() => setSigningIn(false)}
        // Stays where you were: signing in from the header is not a request to
        // go somewhere, and the account link is right there for purchases.
        onSuccess={() => setSigningIn(false)}
      />
    </header>
  );
}
